import React, { useEffect, useState } from 'react';
import {Alert} from 'react-bootstrap';
import { useLocation, Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import config from '../config';
import HeaderNavbar from '../components/HeaderNavbar';
import Spinner from 'react-bootstrap/Spinner';
import Footer from '../components/Footer';
import { getSafePostLoginPath } from '../lib/authRedirect';

import api from '../lib/request';
import './Login.css';

const TWO_FACTOR_METHODS = Object.freeze({
  EMAIL: 'email',
  AUTHENTICATOR: 'authenticator'
});

const EMPTY_TWO_FACTOR_STATE = {
  required: false,
  challengeToken: '',
  method: '',
  destinationPreview: '',
  allowRecoveryCode: false,
  useRecoveryCode: false,
  code: '',
  recoveryCode: ''
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
}

const LoginPage = () => {
  const query = useQuery();
  const goback = query.get('goback');
  const externalError = query.get('externalError');
  const requiredLogin = query.get('rl');

  const { isAuthenticated, isAuthReady, login } = useAuthContext();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFactorState, setTwoFactorState] = useState(EMPTY_TWO_FACTOR_STATE);

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated) return;
    window.location.href = getSafePostLoginPath(goback);
  }, [isAuthenticated, isAuthReady, goback]);

  const completeLogin = async (authToken) => {
    const ok = await login(authToken);
    if (ok) {
      window.location.href = getSafePostLoginPath(goback);
      return;
    }

    setError('Unknown Error. Contact Support.');
    document.getElementById('password')?.focus();
  };

  const applyTwoFactorState = (response) => {
    setTwoFactorState({
      required: true,
      challengeToken: response?.challengeToken || '',
      method: response?.method || '',
      destinationPreview: response?.destinationPreview || '',
      allowRecoveryCode: response?.allowRecoveryCode === true,
      useRecoveryCode: false,
      code: '',
      recoveryCode: ''
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true)
    setError('');
    try {
      const response = await api.post(`/api/v1/auth/login`, { username, password });
      setLoading(false)
      setPassword('');
      if (response?.auth_token) {
        await completeLogin(response.auth_token);
      } else if (response?.requiresTwoFactor) {
        applyTwoFactorState(response);
      } else if (response.error) {
        setError(response.message);
        document.getElementById('password').focus();
      } else {
        console.log(response);
      }
    } catch (err) {
      setLoading(false)
      setPassword('');
      if (err.error) {
        setError(err.message);
        document.getElementById('password').focus();
      }
    }
  };

  const handleTwoFactorVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (twoFactorState.useRecoveryCode) {
        if (!twoFactorState.recoveryCode.trim()) {
          setLoading(false);
          setError('Backup recovery code is required.');
          return;
        }
      } else if (!twoFactorState.code.trim()) {
        setLoading(false);
        setError(twoFactorState.method === TWO_FACTOR_METHODS.AUTHENTICATOR
          ? 'Authenticator app code is required.'
          : 'Verification code is required.');
        return;
      }

      const response = await api.post('/api/v1/auth/login/two-factor/verify', {
        challengeToken: twoFactorState.challengeToken,
        code: twoFactorState.useRecoveryCode ? '' : twoFactorState.code,
        recoveryCode: twoFactorState.useRecoveryCode ? twoFactorState.recoveryCode : ''
      });

      setLoading(false);
      if (response?.auth_token) {
        await completeLogin(response.auth_token);
        return;
      }

      if (response?.error) {
        setError(response.message);
        return;
      }

      console.log(response);
    } catch (err) {
      setLoading(false);
      if (err?.message) {
        setError(err.message);
      } else {
        setError('Unable to verify the second step.');
      }
    }
  };

  const handleTwoFactorResend = async () => {
    if (!twoFactorState.challengeToken) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.post('/api/v1/auth/login/two-factor/resend', {
        challengeToken: twoFactorState.challengeToken
      });
      setLoading(false);
      if (response?.requiresTwoFactor) {
        applyTwoFactorState(response);
        return;
      }
      setError(response?.message || 'Unable to resend the verification email.');
    } catch (err) {
      setLoading(false);
      setError(err?.message || 'Unable to resend the verification email.');
    }
  };

  const handleRestartLogin = () => {
    setTwoFactorState(EMPTY_TWO_FACTOR_STATE);
    setPassword('');
    setError('');
  };

  function getErrorMsg () {
    let displayError = '';
    if (error) {
      displayError = error;
    } else if (externalError === 'expired') {
      displayError = `Your session has expired, please log in again`;
    } else if (externalError === 'forbiden') {
      displayError = `You do not have permissions to operate over this URL. Need to login with a different user?`;
    } else if (externalError === 'unauthorized') {
      displayError = `Access denied.`;
    }
    if (displayError) {
      return (<Alert className="d-grid gap-2 mt-3" variant="danger">{displayError}</Alert>)
    }
  }

   return (
    <>
     <HeaderNavbar/>
     <div className="row">
     {isAuthenticated ? <h3> You are logged in 🎉</h3> : 
          <div className="Auth-form-container">
            <form className="Auth-form auth-account-form" onSubmit={twoFactorState.required ? handleTwoFactorVerify : handleLogin}>
              <div className="Auth-form-content">
               <div style={{display: 'flex', justifyContent: 'center', marginBottom: 10}}>
               <img src="/logo.svg" alt="Logo" width="100" height="100"/>
               </div>
                <h3 className="Auth-form-title">
                  {twoFactorState.required ? 'Two-Factor Authentication' : `Log in${requiredLogin ? " to continue" : ""}`}
                </h3>

                {twoFactorState.required ? (
                  <>
                    <p className="text-center mt-2">
                      {twoFactorState.useRecoveryCode
                        ? 'Enter one of your unused backup recovery codes.'
                        : twoFactorState.method === TWO_FACTOR_METHODS.AUTHENTICATOR
                          ? 'Enter the 6-digit code from your Authenticator app.'
                          : `Enter the 6-digit code sent to ${twoFactorState.destinationPreview || 'your email'}.`}
                    </p>

                    {twoFactorState.allowRecoveryCode ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 auth-two-factor-toggle"
                        onClick={() => setTwoFactorState((prev) => ({
                          ...prev,
                          useRecoveryCode: !prev.useRecoveryCode,
                          code: '',
                          recoveryCode: ''
                        }))}
                      >
                        {twoFactorState.useRecoveryCode ? 'Use the regular verification code instead' : 'Use a backup recovery code instead'}
                      </button>
                    ) : null}

                    {twoFactorState.useRecoveryCode ? (
                      <div className="form-group mt-3">
                        <label>Backup recovery code</label>
                        <input
                          type="text"
                          className="form-control mt-1"
                          placeholder="Enter recovery code"
                          value={twoFactorState.recoveryCode}
                          onChange={(e) => setTwoFactorState((prev) => ({
                            ...prev,
                            recoveryCode: e.target.value
                          }))}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <div className="form-group mt-3">
                        <label>{twoFactorState.method === TWO_FACTOR_METHODS.AUTHENTICATOR ? 'Authenticator app code' : 'Verification code'}</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="one-time-code"
                          className="form-control mt-1"
                          placeholder="Enter code"
                          value={twoFactorState.code}
                          onChange={(e) => setTwoFactorState((prev) => ({
                            ...prev,
                            code: e.target.value
                          }))}
                          autoFocus
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      Not registered yet? <Link to={`${config.prefixPath}/register${goback ? `?goback=${encodeURIComponent(goback)}` : ''}`}>Create account</Link>
                    </div>
                    <div className="form-group mt-3">
                      <label>Email address</label>
                      <input
                        type="email"
                        className="form-control mt-1"
                        placeholder="Enter email"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="form-group mt-3">
                      <label>Password</label>
                      <input
                        id="password"
                        type="password"
                        value={password}
                        className="form-control mt-1"
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                      />
                    </div>
                  </>
                )}

                <div>
                {getErrorMsg()}
                </div>
               
                <div className="d-grid gap-2 mt-3">
                  {loading ? <Spinner animation="border" /> : (
                    <button type="submit" className="btn btn-primary">
                      {twoFactorState.required ? 'Verify' : 'Log in'}
                    </button>
                  )}
                </div>
                {twoFactorState.required ? (
                  <div className="auth-two-factor-actions">
                    {twoFactorState.method === TWO_FACTOR_METHODS.EMAIL && !twoFactorState.useRecoveryCode ? (
                      <button type="button" className="btn btn-link" onClick={handleTwoFactorResend}>
                        Resend code
                      </button>
                    ) : null}
                    <button type="button" className="btn btn-link" onClick={handleRestartLogin}>
                      Start over
                    </button>
                  </div>
                ) : (
                  <p className="text-center mt-2">
                    Forgot your password? <a href={`${config.prefixPath}/forgot-password`}>Reset it</a>
                  </p>
                )}
              </div>
            </form>
          </div>
          }
      </div>
       <Footer/>
      </>
    )
};

export default LoginPage;
